import React, { useContext, useEffect, useState } from 'react';
import Dialog from '#lib/components/dialog.jsx';
import Button from '#lib/components/button.jsx';
import { addClasses, cn, mergeClasses } from '#lib/helpers/class.js';
import Input from '#lib/components/input.jsx';
import { Spinner } from '#lib/components/loading.jsx';
import { Icon } from '@iconify-icon/react';
import Select from '#lib/components/select.jsx';
import { defaultAccessValue, defaultDisplayValue, defaultMakeReactKey } from '#helpers/helpers.js';
import { useImageLibrary } from '#hooks/use-image-library.js';
import FloatingTooltip from '../tooltip.jsx';
import { UserContext } from '#src/index.jsx';
import { useImagePanel } from '#hooks/use-image-panel.js';
import ImageManipulator from './image-manipulator.jsx';

export function ImagePanel(props) {
    const {
        file,
        defaultState,
        coords,
        onlyPreview,
        onChangePreview,
        isOpen,
        onClose,
        width,
        height,
        multiple,
        isSquare,
        ...rest
    } = props;

    const { getLanguage } = useContext(UserContext);

    // It needs to fetch images from the external library api
    const imageLibraryProps = useImageLibrary({ multiple, lang: getLanguage() });

    const imagePanelProps = useImagePanel({
        file,
        defaultState,
        coords,
        handleChangePreview: onChangePreview,
        isOpen,
        onClose,
        ...imageLibraryProps
    });

    const {
        state,
        setIsLoading,
        optionId,
        handleChangePanel,
        handleUpdateFiles,
        handleSubmit,
        handleSelectLibraryImage,
        handleClose
    } = imagePanelProps;

    const handleConfirm = () => {
        if (optionId === 'L') {
            handleSelectLibraryImage(imageLibraryProps.selectedImages);
            return;
        }
        handleSubmit();
    };

    return (
        <Dialog isOpen={isOpen ?? false}>
            <Dialog.Panel static className="flex flex-col max-h-full space-y-4 select-none">
                <div className="pr-4">
                    <Dialog.CloseButton
                        onClick={() => {
                            handleClose(true);
                            setIsLoading(false);
                        }}
                    />
                    <b>{_t.edit.imagePanelText()}</b>
                    <p className="pr-2 mt-1 text-sm">
                        {state === 'selecting' && _t.edit.imagePanelSubText()}
                        {state === 'uploading' && _t.edit.imagePanelSubText()}
                        {state === 'editing' && _t.edit.imagePanelDescription()}
                    </p>
                </div>
                {state === 'selecting' && (
                    <SelectionSection onClick={handleChangePanel} handleUpdateFiles={handleUpdateFiles} />
                )}
                {state === 'uploading' && (
                    <LoaderSection
                        {...props}
                        {...imagePanelProps}
                        {...imageLibraryProps}
                        handleConfirm={handleConfirm}
                    />
                )}
                {state === 'editing' && <EditSection {...props} {...imagePanelProps} />}
            </Dialog.Panel>
        </Dialog>
    );
}

// state === "selecting"
function SelectionSection({ onClick, handleUpdateFiles }) {
    const OPTIONS = [
        {
            id: 'U',
            value: _t.global.importOptions.device()
        },
        {
            id: 'L',
            value: _t.global.importOptions.library()
        },
        {
            id: 'E',
            value: _t.global.importOptions.url()
        },
        {
            id: 'D',
            value: _t.global.importOptions.aiImage()
        }
    ];

    return (
        <div className="flex justify-between gap-4">
            {OPTIONS.map((option) => {
                return (
                    <div
                        key={option.id}
                        className="relative flex flex-col items-center w-24 gap-3 px-2 py-4 border rounded-lg cursor-pointer bg-slate-100 border-slate-200 hover:bg-primary-50 hover:border-primary-400"
                        onClick={() => onClick(option.id)}
                    >
                        {option.id === 'U' && (
                            <>
                                <Icon
                                    icon="material-symbols:computer-outline"
                                    width="1.2rem"
                                    height="1.2rem"
                                    className="pt-[3px]"
                                />
                                <input
                                    type="file"
                                    onChange={handleUpdateFiles}
                                    className="absolute top-0 left-0 w-full h-full opacity-0 cursor-pointer"
                                    accept="image/png, image/jpeg"
                                />
                            </>
                        )}
                        {option.id === 'L' && (
                            <Icon
                                icon="clarity:image-gallery-solid"
                                width="1.4rem"
                                height="1.4rem"
                                className="pt-[3px]"
                            />
                        )}
                        {option.id === 'E' && (
                            <Icon icon="ph:globe" width="1.4rem" height="1.4rem" className="pt-[3px]" />
                        )}
                        {option.id === 'D' && (
                            <Icon icon="mdi:robot-outline" width="1.2rem" height="1.2rem" className="pt-[3px]" />
                        )}

                        <p className="text-xs">{option.value}</p>
                    </div>
                );
            })}
        </div>
    );
}

// state === "uploading"
function LoaderSection(props) {
    const {
        optionId,
        isError,
        onlyPreview,
        editingFile,
        isEdited,
        isLoading,
        width,
        height,
        isSquare,
        libraries,
        images,
        handleSelectLibrary,
        currentLibrary,
        inputValue,
        hasError,
        handleChangeText,
        handleClear,
        handleConfirm,
        handleGenerateImage
    } = props;

    const baseStyle = {
        width,
        height,
        marginLeft: 'auto',
        marginRight: 'auto'
    };
    const style = {
        ...baseStyle,
        aspectRatio: isSquare ? '1/1' : 'none'
    };

    return (
        <>
            {optionId && optionId !== 'U' && (
                <div className="flex items-start justify-end gap-2">
                    {optionId === 'L' && (
                        <>
                            <Select.Full
                                options={libraries}
                                makeReactKey={defaultMakeReactKey}
                                accessValue={defaultAccessValue}
                                displayValue={defaultDisplayValue}
                                onChange={handleSelectLibrary}
                                value={currentLibrary}
                                className={addClasses('cursor-pointer')}
                                placeholder={_t.edit.selectLibrary()}
                                name="library"
                                inPortal={false}
                            />
                            <Input.Full
                                className="w-full h-full"
                                placeholder={_t.edit.enterKeyword()}
                                value={inputValue}
                                hasError={hasError}
                                onChange={handleChangeText}
                            />
                        </>
                    )}
                    {optionId === 'E' && (
                        <Input.Full
                            className="w-full h-full"
                            placeholder={_t.edit.enterUrl()}
                            value={inputValue}
                            hasError={hasError}
                            onChange={handleChangeText}
                        />
                    )}
                    {optionId === 'D' && (
                        <Input.Full
                            className="w-full h-full"
                            placeholder={_t.edit.writePrompt()}
                            value={inputValue}
                            hasError={hasError}
                            onChange={handleChangeText}
                        />
                    )}
                    <Button look="primary" type="submit" disabled={isLoading} onClick={handleGenerateImage}>
                        {optionId === 'E' && _t.global.add()}
                        {optionId === 'L' && _t.global.search()}
                        {optionId === 'D'
                            ? editingFile
                                ? _t.global.regenerate()
                                : isLoading
                                ? _t.global.loading()
                                : _t.global.generate()
                            : null}
                    </Button>
                </div>
            )}
            <div
                className="flex justify-center w-full overflow-hidden rounded-md bg-neutral-100"
                style={{
                    width: '100%',
                    height
                }}
            >
                <div
                    className="relative flex justify-center overflow-hidden border-2 rounded-md bg-neutral-50 border-neutral-300"
                    style={style}
                >
                    {isLoading && (
                        <div className="absolute inset-0 flex items-center justify-center h-full">
                            <Spinner />
                        </div>
                    )}
                    {isError && (
                        <div className="absolute inset-0 flex items-center justify-center h-full">
                            <p className="flex items-center justify-center w-full h-full p-4 text-lg font-bold text-neutral-600">
                                {_t.errors.generationImage()}
                            </p>
                        </div>
                    )}
                    {optionId === 'L' && images?.length > 0 && <ImageLibrarySection style={style} {...props} />}
                </div>
            </div>

            <div className="flex justify-end gap-2">
                {!onlyPreview && (
                    <Button type="submit" onClick={handleClear} disabled={isLoading}>
                        {editingFile ? _t.global.change() : _t.global.cancel()}
                    </Button>
                )}
                <Button look="primary" type="submit" onClick={handleConfirm} disabled={!isEdited}>
                    {_t.global.confirm()}
                </Button>
            </div>
        </>
    );
}

// state === "editing"
function EditSection(props) {
    const {
        editingFile,
        setState,
        handleInitCoords,
        handleClear,
        handleSubmit,
        width,
        height,
        isLoading,
        isEdited,
        isSquare,
        rounded,
        handleDeleteEditingFile
    } = props;

    const baseStyle = {
        width,
        height,
        marginLeft: 'auto',
        marginRight: 'auto'
    };
    const style = {
        ...baseStyle,
        aspectRatio: isSquare ? '1/1' : 'none'
    };

    return (
        <>
            <div className="flex justify-center w-full overflow-hidden rounded-md bg-neutral-100">
                <div
                    className="relative flex items-center justify-center overflow-hidden border rounded-md bg-neutral-50 border-neutral-300"
                    style={style}
                >
                    {editingFile && (
                        <ImageManipulator
                            image={editingFile}
                            containerWidth={width}
                            containerHeight={height}
                            initialState={editingFile?.coords}
                            onTransformChange={handleInitCoords}
                            rounded={rounded}
                            setState={setState}
                            onDelete={handleDeleteEditingFile}
                        />
                    )}
                </div>
            </div>
            <div className="flex justify-end gap-2">
                <Button type="submit" onClick={handleClear} disabled={isLoading}>
                    {editingFile ? _t.global.change() : _t.global.cancel()}
                </Button>
                <Button look="primary" type="submit" onClick={handleSubmit} disabled={!isEdited}>
                    {_t.global.confirm()}
                </Button>
            </div>
        </>
    );
}

function ImageLibrarySection(props) {
    const {
        inputValue,
        images,
        selectedImages,
        lastSearch,
        loading,
        error,
        fetchImagesFromLibrary,
        selectImage,
        library,
        className
    } = props;

    const { lastPage } = lastSearch;
    const [hoveredImg, setHoveredImg] = useState(null);
    const [loadBtn, setLoadBtn] = useState(false);

    const onClick = async () => {
        setLoadBtn(true);
        await fetchImagesFromLibrary(inputValue, lastPage + 1, (e) => console.log(e));
    };

    useEffect(() => {
        setLoadBtn(false);
    }, [images]);

    return (
        <div
            className={mergeClasses(
                'relative w-full h-full grid grid-cols-3 gap-1 bg-neutral-100 overflow-auto',
                className
            )}
        >
            {!images.length && !loading && !error && (
                <div className="absolute top-0 left-0 flex items-center justify-center w-full h-full p-2">
                    <p className="text-center text-neutral-700">{_t.edit.promptToSearch()}</p>
                </div>
            )}

            {loading && (
                <div className="absolute inset-0 flex items-center justify-center h-full">
                    <Spinner />
                </div>
            )}
            {images &&
                images?.map((image) => {
                    const isSelected = selectedImages.some((img) => img.id === image.id);
                    return (
                        <FloatingTooltip
                            key={image?.id}
                            content={`created by '${image.author}' on ${library}`}
                            placement={'top'}
                        >
                            <div
                                className="col-span-1 relative pt-[56%] overflow-hidden"
                                onMouseOver={() => setHoveredImg(image.id)}
                                onMouseOut={() => setHoveredImg(null)}
                            >
                                <Thumbnail src={image.thumbnailSrc} />
                                <div
                                    className={cn(
                                        'transition-all absolute w-full h-full top-0 cursor-pointer',
                                        hoveredImg !== image.id || isSelected ? 'bg-[#00000044]' : 'bg-transparent',
                                        isSelected && 'bg-transparent border-[6px] border-primary-300'
                                    )}
                                    onClick={() => selectImage(image)}
                                ></div>
                            </div>
                        </FloatingTooltip>
                    );
                })}

            {!error && images && images.length !== 0 && (
                <div className="flex justify-center col-span-3">
                    <Button className="w-full my-2 font-bold text-primary-600" busy={loadBtn} onClick={onClick}>
                        {_t.components.table.loadMore()}
                    </Button>
                </div>
            )}
            {error && !loading && (
                <div className="w-full col-span-3">
                    <p className="flex items-center justify-center w-full h-full p-4 text-lg font-bold text-neutral-600">
                        {_t.components.table.noMore()}
                    </p>
                </div>
            )}
        </div>
    );
}

function Thumbnail(props) {
    const { src, ...rest } = props;
    return <img className="absolute top-0 object-cover object-center w-full h-full" src={src} {...rest} />;
}
